import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  Typography,
  Button,
  Select,
  Input,
  InputNumber,
  Slider,
  Tag,
  Popconfirm,
  Empty,
  Checkbox,
  Row,
  Col,
  Upload,
  Spin,
  Modal,
  Avatar,
  Space,
  Badge,
  Tooltip,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  OrderedListOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  PaperClipOutlined,
  DownloadOutlined,
  ToolOutlined,
  UploadOutlined,
  DollarOutlined,
  SettingOutlined,
  UserOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  FileOutlined,
  MessageOutlined,
  ArrowLeftOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { actionApi, ActionValidationError } from '../../../utils/actionApi';
import { getSuccessMessage } from '../../../utils/actionMessageTranslator';
import { numberFormatter, numberParser } from '../../../utils/numberUtils';
import { employeesApi } from '../../../utils/api';
import { toJalali, toGregorian } from '../../../utils/dateUtils';
import PersianDatePicker from '../../../components/PersianDatePicker';
import moment from 'moment-jalaali';
import { CardListSkeleton, CommentsSkeleton } from '../../../components/SkeletonLoaders';
import MentionTextArea, {
  convertMentionsToEmails,
  renderContentWithMentions,
} from '../../../components/MentionTextArea';
import '../../../components/MentionTextArea.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const PRIORITY_OPTIONS = [
  { value: 'فوری_و_اضطراری', label: 'فوری و اضطراری', color: 'red' },
  { value: 'بسیار_مهم', label: 'بسیار مهم', color: 'volcano' },
  { value: 'مهم', label: 'مهم', color: 'gold' },
  { value: 'کم_اهمیت', label: 'کم اهمیت', color: 'blue' },
  { value: 'غیرضروری', label: 'غیرضروری', color: 'default' },
];

const STATUS_OPTIONS = [
  { value: 'باز', label: 'باز', color: 'blue' },
  { value: 'در_حال_برنامه_ریزی', label: 'در حال برنامه‌ریزی', color: 'gold' },
  { value: 'در_حال_انجام', label: 'در حال انجام', color: 'green' },
  { value: 'بسته', label: 'بسته', color: 'default' },
];

const mapPriorityTag = (value) => {
  const found = PRIORITY_OPTIONS.find((p) => p.value === value);
  if (!found) return null;
  return (
    <Tag color={found.color} style={{ fontFamily: 'CustomFont', margin: 0 }}>
      {found.label}
    </Tag>
  );
};

const mapStatusTag = (value) => {
  const found = STATUS_OPTIONS.find((s) => s.value === value);
  if (!found) return null;
  return (
    <Tag color={found.color} style={{ fontFamily: 'CustomFont', margin: 0 }}>
      {found.label}
    </Tag>
  );
};

// آیکون و رنگ برای هر نوع منبع
const getResourceTypeConfig = (type) => {
  const configs = {
    بودجه: {
      icon: <DollarOutlined />,
      color: '#10b981',
      bgColor: '#ecfdf5',
      darkBgColor: 'rgba(16, 185, 129, 0.1)',
    },
    ابزار: {
      icon: <ToolOutlined />,
      color: '#f59e0b',
      bgColor: '#fffbeb',
      darkBgColor: 'rgba(245, 158, 11, 0.1)',
    },
    نیروی_انسانی: {
      icon: <TeamOutlined />,
      color: '#3b82f6',
      bgColor: '#eff6ff',
      darkBgColor: 'rgba(59, 130, 246, 0.1)',
    },
    تجهیزات: {
      icon: <SettingOutlined />,
      color: '#8b5cf6',
      bgColor: '#f5f3ff',
      darkBgColor: 'rgba(139, 92, 246, 0.1)',
    },
    پیمانکار: {
      icon: <UserOutlined />,
      color: '#ec4899',
      bgColor: '#fdf2f8',
      darkBgColor: 'rgba(236, 72, 153, 0.1)',
    },
    سایر: {
      icon: <AppstoreOutlined />,
      color: '#6b7280',
      bgColor: '#f9fafb',
      darkBgColor: 'rgba(107, 114, 128, 0.1)',
    },
  };
  return (
    configs[type] || {
      icon: <DatabaseOutlined />,
      color: '#6b7280',
      bgColor: '#f9fafb',
      darkBgColor: 'rgba(107, 114, 128, 0.1)',
    }
  );
};

export default function ActionItemsTab({ actionId, primaryButtonClass }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);

  // RACI state for items
  const [raciModalOpen, setRaciModalOpen] = useState(false);
  const [raciItemId, setRaciItemId] = useState(null);
  const [itemRacis, setItemRacis] = useState([]);
  const [raciLoading, setRaciLoading] = useState(false);
  const [erpEmployees, setErpEmployees] = useState([]);
  const [erpLoading, setErpLoading] = useState(false);
  const [selectedErpUserId, setSelectedErpUserId] = useState(undefined);
  const [selectedActionUserId, setSelectedActionUserId] = useState(undefined);
  const [selectedRaciRole, setSelectedRaciRole] = useState(undefined);

  // Resources state for items
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [resourceItemId, setResourceItemId] = useState(null);
  const [itemResources, setItemResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [editingResourceId, setEditingResourceId] = useState(null);
  const [resourceType, setResourceType] = useState(undefined);
  const [resourceDescription, setResourceDescription] = useState('');
  const [resourceStatus, setResourceStatus] = useState('planned');
  const [resourceSubmitting, setResourceSubmitting] = useState(false);
  
  // Dynamic fields برای بودجه
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetCurrency, setBudgetCurrency] = useState('');
  const [budgetCategory, setBudgetCategory] = useState('');
  const [budgetFiscalYear, setBudgetFiscalYear] = useState('');
  const [budgetAllocatedAmount, setBudgetAllocatedAmount] = useState('');
  const [budgetUsedAmount, setBudgetUsedAmount] = useState('');
  
  // Dynamic fields برای نیروی انسانی
  const [personCount, setPersonCount] = useState('');
  const [personHours, setPersonHours] = useState('');
  const [personSkillLevel, setPersonSkillLevel] = useState('');
  const [personPosition, setPersonPosition] = useState('');
  const [personDepartment, setPersonDepartment] = useState('');
  const [personAllocatedAmount, setPersonAllocatedAmount] = useState('');
  const [personUsedAmount, setPersonUsedAmount] = useState('');
  
  // Dynamic fields برای تجهیزات
  const [equipmentName, setEquipmentName] = useState('');
  const [equipmentQuantity, setEquipmentQuantity] = useState('');
  const [equipmentModel, setEquipmentModel] = useState('');
  const [equipmentSpecifications, setEquipmentSpecifications] = useState('');
  const [equipmentCondition, setEquipmentCondition] = useState('');
  const [equipmentAllocatedAmount, setEquipmentAllocatedAmount] = useState('');
  const [equipmentUsedAmount, setEquipmentUsedAmount] = useState('');
  
  // Dynamic fields برای ابزار
  const [toolName, setToolName] = useState('');
  const [toolQuantity, setToolQuantity] = useState('');
  const [toolCondition, setToolCondition] = useState('');
  const [toolSpecifications, setToolSpecifications] = useState('');
  const [toolAllocatedAmount, setToolAllocatedAmount] = useState('');
  const [toolUsedAmount, setToolUsedAmount] = useState('');
  
  // Dynamic fields برای پیمانکار
  const [contractorName, setContractorName] = useState('');
  const [contractValue, setContractValue] = useState('');
  const [contractDuration, setContractDuration] = useState('');
  const [contractDurationUnit, setContractDurationUnit] = useState(undefined);
  const [contractType, setContractType] = useState('');
  const [contractorAllocatedAmount, setContractorAllocatedAmount] = useState('');
  const [contractorUsedAmount, setContractorUsedAmount] = useState('');
  
  // Dynamic fields برای سایر
  const [customFields, setCustomFields] = useState([]);
  const [otherAllocatedAmount, setOtherAllocatedAmount] = useState('');
  const [otherUsedAmount, setOtherUsedAmount] = useState('');

  // Attachments state for items
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [attachmentItemId, setAttachmentItemId] = useState(null);
  const [itemAttachments, setItemAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentFileList, setAttachmentFileList] = useState([]);
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [editingAttachmentId, setEditingAttachmentId] = useState(null);
  const [editingAttachmentDescription, setEditingAttachmentDescription] = useState('');
  const [editingAttachmentLoading, setEditingAttachmentLoading] = useState(false);

  // Comments state for items
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentItemId, setCommentItemId] = useState(null);
  const [itemComments, setItemComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [commentParentId, setCommentParentId] = useState(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentDeletingId, setCommentDeletingId] = useState(null);
  const [isMentionSyncing, setIsMentionSyncing] = useState(false);
  const mentionRef = useRef(null);
  const [mentionMappings, setMentionMappings] = useState([]);
  
  // State برای expand/collapse توضیحات هر آیتم
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState();
  const [status, setStatus] = useState();
  const [percentComplete, setPercentComplete] = useState(0);
  const [weight, setWeight] = useState();
  const [plannedStart, setPlannedStart] = useState('');
  const [plannedEnd, setPlannedEnd] = useState('');

  const loadItems = async () => {
    if (!actionId) return;
    setLoading(true);
    try {
      const res = await actionApi.getActionItems(actionId);
      const list = res?.data || res?.data?.data || [];
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت لیست آیتم‌ها');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setTitle('');
    setDescription('');
    setPriority(undefined);
    setStatus(undefined);
    setPercentComplete(0);
    setWeight(undefined);
    setPlannedStart('');
    setPlannedEnd('');
  };

  const openCreateForm = () => {
    resetForm();
  };

  const openEditForm = async (itemId) => {
    if (!actionId || !itemId) return;
    setFormLoading(true);
    try {
      const res = await actionApi.getActionItem(actionId, itemId);
      const data = res?.data || res?.data?.data || {};
      setEditingId(data.id);
      setTitle(data.title || '');
      setDescription(data.description || '');
      setPriority(data.priority || undefined);
      setStatus(data.status || undefined);
      setPercentComplete(
        typeof data.percent_complete === 'number'
          ? data.percent_complete
          : Number(data.percent_complete || 0)
      );
      setWeight(data.weight !== undefined && data.weight !== null ? Number(data.weight) : undefined);
      // تبدیل فرمت تاریخ از API به فرمت PersianDatePicker
      // از API: "1404/10/07 | 14:00" → برای PersianDatePicker: "1404/10/07 14:00"
      const convertApiDateToPicker = (dateStr) => {
        if (!dateStr) return null;
        // جایگزین " | " با فاصله برای PersianDatePicker
        return dateStr.replace(/\s*\|\s*/g, ' ');
      };
      
      setPlannedStart(convertApiDateToPicker(data.planned_start_date));
      setPlannedEnd(convertApiDateToPicker(data.planned_end_date));
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت اطلاعات آیتم');
    } finally {
      setFormLoading(false);
    }
  };

  const openRaciModalForItem = async (itemId) => {
    if (!actionId || !itemId) return;
    setRaciItemId(itemId);
    setRaciModalOpen(true);
    setSelectedErpUserId(undefined);
    setSelectedActionUserId(undefined);
    setSelectedRaciRole(undefined);
    setItemRacis([]);
    setRaciLoading(true);
    try {
      await Promise.all([loadItemRacis(itemId), fetchErpEmployees()]);
    } finally {
      setRaciLoading(false);
    }
  };

  const closeRaciModal = () => {
    setRaciModalOpen(false);
    setRaciItemId(null);
    setItemRacis([]);
    setSelectedErpUserId(undefined);
    setSelectedActionUserId(undefined);
    setSelectedRaciRole(undefined);
  };

  const handleDelete = async (itemId) => {
    if (!actionId || !itemId) return;
    setLoading(true);
    try {
      const res = await actionApi.deleteActionItem(actionId, itemId);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'آیتم با موفقیت حذف شد'));
        await loadItems();
        if (editingId === itemId) {
          resetForm();
        }
      } else {
        message.error(res?.message || 'خطا در حذف آیتم');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در حذف آیتم');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!actionId) return;
    const trimmedTitle = (title || '').trim();
    const trimmedDesc = (description || '').trim();

    if (!trimmedTitle) {
      message.error('عنوان آیتم الزامی است');
      return;
    }
    if (trimmedTitle.length > 255) {
      message.error('عنوان آیتم حداکثر ۲۵۵ کاراکتر است');
      return;
    }

    if (!priority) {
      message.error('انتخاب اولویت آیتم الزامی است');
      return;
    }

    if (!status) {
      message.error('انتخاب وضعیت آیتم الزامی است');
      return;
    }

    if (trimmedDesc.length > 2000) {
      message.error('توضیحات آیتم حداکثر ۲۰۰۰ کاراکتر است');
      return;
    }

    const payload = {
      title: trimmedTitle,
      priority,
      status,
    };

    if (trimmedDesc) payload.description = trimmedDesc;
    if (percentComplete !== undefined && percentComplete !== null) {
      payload.percent_complete = Number(percentComplete);
    }
    if (weight !== undefined && weight !== null && weight !== '') {
      payload.weight = Number(weight);
    }
    // تبدیل تاریخ‌ها به فرمت میلادی با ساعت برای API: "2025-12-28 14:00:00"
    const formatDateForApi = (val) => {
      if (!val) return null;
      
      // اگر DateObject از react-multi-date-picker باشد (که شامل ساعت است)
      if (val?.toDate && typeof val.toDate === 'function') {
        const dateObj = val.toDate();
        const m = moment(dateObj);
        if (m && m.isValid()) {
          const hasTime = val.hour !== undefined || val.minute !== undefined || 
                          (dateObj.getHours() !== 0 || dateObj.getMinutes() !== 0 || dateObj.getSeconds() !== 0);
          if (hasTime) {
            return m.format('YYYY-MM-DD HH:mm:ss');
          }
          return m.format('YYYY-MM-DD');
        }
      }
      
      // اگر رشته شامل ساعت است (مثلاً "1403/10/15 14:30" یا "1403/10/15 | 14:00")
      if (typeof val === 'string') {
        let datePart, timePart;
        
        if (val.includes(' | ')) {
          const parts = val.split(' | ');
          datePart = parts[0];
          timePart = parts[1] || '';
        } else if (val.includes(' ')) {
          const parts = val.split(' ');
          datePart = parts[0];
          timePart = parts[1] || '';
        } else {
          datePart = val;
          timePart = '';
        }
        
        const gregorianDate = toGregorian(datePart, 'YYYY-MM-DD');
        if (!gregorianDate) return null;
        
        if (timePart) {
          const timeParts = timePart.split(':');
          if (timeParts.length === 2) {
            return `${gregorianDate} ${timePart}:00`;
          }
          return `${gregorianDate} ${timePart}`;
        }
        
        return gregorianDate;
      }
      
      return toGregorian(val, 'YYYY-MM-DD');
    };
    
    if (plannedStart) payload.planned_start_date = formatDateForApi(plannedStart);
    if (plannedEnd) payload.planned_end_date = formatDateForApi(plannedEnd);

    setFormLoading(true);
    try {
      let res;
      if (editingId) {
        res = await actionApi.updateActionItem(actionId, editingId, payload);
      } else {
        res = await actionApi.createActionItem(actionId, payload);
      }

      if (res?.success) {
        message.success(getSuccessMessage(res.message, editingId ? 'آیتم به‌روزرسانی شد' : 'آیتم ایجاد شد'));
        resetForm();
        await loadItems();
      } else {
        message.error(res?.message || 'خطا در ثبت آیتم');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        const errors = error.errors || {};
        const firstKey = Object.keys(errors)[0];
        const firstMsg = firstKey ? errors[firstKey][0] : null;
        message.error(firstMsg || error.message || 'خطا در اعتبارسنجی آیتم');
      } else {
        message.error(error.message || 'خطا در ثبت آیتم');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleStart = async (itemId) => {
    if (!actionId || !itemId) return;
    setStartingId(itemId);
    try {
      const res = await actionApi.startActionItem(actionId, itemId);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'آغاز آیتم با موفقیت ثبت شد'));
        await loadItems();
      } else {
        message.error(res?.message || 'خطا در آغاز آیتم');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در آغاز آیتم');
    } finally {
      setStartingId(null);
    }
  };

  const handleToggleComplete = async (item) => {
    if (!actionId || !item?.id) return;
    // اگر قبلاً actual_end_date دارد یعنی انجام شده؛ فعلاً اجازه لغو تیک را نمی‌دهیم
    if (item.actual_end_date) return;

    setCompletingId(item.id);
    try {
      const res = await actionApi.completeActionItem(actionId, item.id);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'آیتم به عنوان انجام‌شده علامت‌گذاری شد'));
        await loadItems();
      } else {
        message.error(res?.message || 'خطا در علامت‌گذاری آیتم');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در علامت‌گذاری آیتم');
    } finally {
      setCompletingId(null);
    }
  };

  // ---------- RACI for items ----------
  const loadItemRacis = async (itemId) => {
    if (!actionId || !itemId) return;
    setRaciLoading(true);
    try {
      const res = await actionApi.getActionItemRacis(actionId, itemId);
      const list = res?.data || res?.data?.data || [];
      setItemRacis(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در دریافت RACI آیتم');
    } finally {
      setRaciLoading(false);
    }
  };

  const fetchErpEmployees = async (search = '') => {
    setErpLoading(true);
    try {
      const filters = {};
      if (search) {
        filters.name = search;
      }
      const res = await employeesApi.getAll(filters);
      const list = (res.data || []).map((item) => ({
        id: item.id,
        name: item.name || '--',
        email: item.email || '--',
      }));
      setErpEmployees(list);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت لیست کارمندان');
    } finally {
      setErpLoading(false);
    }
  };

  const syncUserAndGetActionUserId = async (erpUserId) => {
    try {
      const res = await actionApi.syncUserFromErp(erpUserId);
      if (res?.success && res?.data?.id) {
        return res.data.id;
      }
      throw new Error(res?.message || 'خطا در همگام‌سازی کاربر');
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const handleAddItemRaci = async () => {
    if (!actionId || !raciItemId) return;
    if (!selectedActionUserId) {
      message.error('ابتدا کاربر را انتخاب کنید');
      return;
    }
    if (!selectedRaciRole) {
      message.error('ابتدا نقش RACI را انتخاب کنید');
      return;
    }

    setRaciLoading(true);
    try {
      const res = await actionApi.createActionItemRacis(actionId, raciItemId, [
        { user_id: selectedActionUserId, raci_role: selectedRaciRole },
      ]);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'عضو RACI با موفقیت اضافه شد'));
        setSelectedErpUserId(undefined);
        setSelectedActionUserId(undefined);
        setSelectedRaciRole(undefined);
        await loadItemRacis(raciItemId);
      } else {
        message.error(res?.message || 'خطا در افزودن RACI آیتم');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        message.error(error.message || 'خطا در اعتبارسنجی RACI آیتم');
      } else {
        message.error(error.message || 'خطا در افزودن RACI آیتم');
      }
    } finally {
      setRaciLoading(false);
    }
  };

  const handleDeleteItemRaci = async (raciId) => {
    if (!actionId || !raciItemId || !raciId) return;
    setRaciLoading(true);
    try {
      const res = await actionApi.deleteActionItemRaci(actionId, raciItemId, raciId);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'RACI آیتم حذف شد'));
        await loadItemRacis(raciItemId);
      } else {
        message.error(res?.message || 'خطا در حذف RACI آیتم');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در حذف RACI آیتم');
    } finally {
      setRaciLoading(false);
    }
  };

  // ---------- Resources for items ----------
  const loadItemResources = async (itemId) => {
    if (!actionId || !itemId) return;
    setResourcesLoading(true);
    try {
      const res = await actionApi.getActionItemResources(actionId, itemId);
      // فرمت جدید: data.items برای لیست منابع
      const items = res?.data?.items || res?.data?.data || res?.data || [];
      setItemResources(Array.isArray(items) ? items : []);
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در دریافت منابع آیتم');
    } finally {
      setResourcesLoading(false);
    }
  };

  const resetAllResourceFields = () => {
    // فقط فیلدهای dynamic را reset می‌کنیم، نه resourceType را
    // بودجه
    setBudgetAmount('');
    setBudgetCurrency('');
    setBudgetCategory('');
    setBudgetFiscalYear('');
    setBudgetAllocatedAmount('');
    setBudgetUsedAmount('');
    
    // نیروی انسانی
    setPersonCount('');
    setPersonHours('');
    setPersonSkillLevel('');
    setPersonPosition('');
    setPersonDepartment('');
    setPersonAllocatedAmount('');
    setPersonUsedAmount('');
    
    // تجهیزات
    setEquipmentName('');
    setEquipmentQuantity('');
    setEquipmentModel('');
    setEquipmentSpecifications('');
    setEquipmentCondition('');
    setEquipmentAllocatedAmount('');
    setEquipmentUsedAmount('');
    
    // ابزار
    setToolName('');
    setToolQuantity('');
    setToolCondition('');
    setToolSpecifications('');
    setToolAllocatedAmount('');
    setToolUsedAmount('');
    
    // پیمانکار
    setContractorName('');
    setContractValue('');
    setContractDuration('');
    setContractDurationUnit(undefined);
    setContractType('');
    setContractorAllocatedAmount('');
    setContractorUsedAmount('');
    
    // سایر
    setCustomFields([]);
    setOtherAllocatedAmount('');
    setOtherUsedAmount('');
  };

  const resetAllResourceFieldsAndType = () => {
    setEditingResourceId(null);
    setResourceType(undefined);
    setResourceDescription('');
    setResourceStatus('planned');
    resetAllResourceFields();
  };

  const openResourceModalForItem = async (itemId) => {
    if (!actionId || !itemId) return;
    setResourceItemId(itemId);
    setResourceModalOpen(true);
    resetAllResourceFieldsAndType();
    await loadItemResources(itemId);
  };

  const closeResourceModal = () => {
    setResourceModalOpen(false);
    setResourceItemId(null);
    setItemResources([]);
    resetAllResourceFieldsAndType();
  };

  const buildDynamicFields = () => {
    const dynamicFields = {};
    
    if (resourceType === 'بودجه') {
      if (!budgetAmount || Number(budgetAmount) <= 0) {
        message.error('مبلغ بودجه الزامی است و باید عددی مثبت باشد');
        return null;
      }
      if (!budgetCurrency) {
        message.error('واحد پول الزامی است');
        return null;
      }
      dynamicFields.amount = parseFloat(budgetAmount);
      dynamicFields.currency = budgetCurrency;
      if (budgetCategory) dynamicFields.budget_category = budgetCategory;
      if (budgetFiscalYear) dynamicFields.fiscal_year = parseInt(budgetFiscalYear);
      if (budgetAllocatedAmount) dynamicFields.allocated_amount = parseFloat(budgetAllocatedAmount);
      if (budgetUsedAmount) dynamicFields.used_amount = parseFloat(budgetUsedAmount);
    } else if (resourceType === 'نیروی_انسانی') {
      if (!personCount || Number(personCount) < 1) {
        message.error('تعداد نفرات الزامی است و باید حداقل 1 باشد');
        return null;
      }
      dynamicFields.person_count = parseInt(personCount);
      if (personHours) dynamicFields.hours = parseFloat(personHours);
      if (personSkillLevel) dynamicFields.skill_level = personSkillLevel;
      if (personPosition) dynamicFields.position = personPosition;
      if (personDepartment) dynamicFields.department = personDepartment;
      if (personAllocatedAmount) dynamicFields.allocated_amount = parseFloat(personAllocatedAmount);
      if (personUsedAmount) dynamicFields.used_amount = parseFloat(personUsedAmount);
    } else if (resourceType === 'تجهیزات') {
      if (!equipmentName) {
        message.error('نام تجهیزات الزامی است');
        return null;
      }
      if (!equipmentQuantity || Number(equipmentQuantity) < 1) {
        message.error('تعداد تجهیزات الزامی است و باید حداقل 1 باشد');
        return null;
      }
      dynamicFields.equipment_name = equipmentName;
      dynamicFields.quantity = parseInt(equipmentQuantity);
      if (equipmentModel) dynamicFields.model = equipmentModel;
      if (equipmentSpecifications) dynamicFields.specifications = equipmentSpecifications;
      if (equipmentCondition) dynamicFields.condition = equipmentCondition;
      if (equipmentAllocatedAmount) dynamicFields.allocated_amount = parseFloat(equipmentAllocatedAmount);
      if (equipmentUsedAmount) dynamicFields.used_amount = parseFloat(equipmentUsedAmount);
    } else if (resourceType === 'ابزار') {
      if (!toolName) {
        message.error('نام ابزار الزامی است');
        return null;
      }
      if (!toolQuantity || Number(toolQuantity) < 1) {
        message.error('تعداد ابزار الزامی است و باید حداقل 1 باشد');
        return null;
      }
      dynamicFields.tool_name = toolName;
      dynamicFields.quantity = parseInt(toolQuantity);
      if (toolCondition) dynamicFields.condition = toolCondition;
      if (toolSpecifications) dynamicFields.specifications = toolSpecifications;
      if (toolAllocatedAmount) dynamicFields.allocated_amount = parseFloat(toolAllocatedAmount);
      if (toolUsedAmount) dynamicFields.used_amount = parseFloat(toolUsedAmount);
    } else if (resourceType === 'پیمانکار') {
      if (!contractorName) {
        message.error('نام پیمانکار الزامی است');
        return null;
      }
      if (!contractValue || Number(contractValue) <= 0) {
        message.error('مبلغ قرارداد الزامی است و باید عددی مثبت باشد');
        return null;
      }
      dynamicFields.contractor_name = contractorName;
      dynamicFields.contract_value = parseFloat(contractValue);
      if (contractDuration) dynamicFields.contract_duration = parseInt(contractDuration);
      if (contractDurationUnit) dynamicFields.contract_duration_unit = contractDurationUnit;
      if (contractType) dynamicFields.contract_type = contractType;
      if (contractorAllocatedAmount) dynamicFields.allocated_amount = parseFloat(contractorAllocatedAmount);
      if (contractorUsedAmount) dynamicFields.used_amount = parseFloat(contractorUsedAmount);
    } else if (resourceType === 'سایر') {
      if (customFields.length > 0) {
        dynamicFields.custom_fields = customFields.filter(cf => cf.key && cf.value);
      }
      if (otherAllocatedAmount) dynamicFields.allocated_amount = parseFloat(otherAllocatedAmount);
      if (otherUsedAmount) dynamicFields.used_amount = parseFloat(otherUsedAmount);
    }
    
    return dynamicFields;
  };

  const openEditResource = async (resourceId) => {
    if (!actionId || !resourceItemId || !resourceId) return;
    setResourceSubmitting(true);
    try {
      const res = await actionApi.getActionItemResource(actionId, resourceItemId, resourceId);
      const resource = res?.data || {};
      const dynamicFields = resource.dynamic_fields || {};
      
      setEditingResourceId(resourceId);
      setResourceType(resource.resource_type || undefined);
      setResourceDescription(resource.description || '');
      setResourceStatus(resource.status || 'planned');
      
      // پر کردن فیلدهای dynamic بر اساس نوع منبع
      if (resource.resource_type === 'بودجه') {
        setBudgetAmount(dynamicFields.amount ? String(dynamicFields.amount) : '');
        setBudgetCurrency(dynamicFields.currency || '');
        setBudgetCategory(dynamicFields.budget_category || '');
        setBudgetFiscalYear(dynamicFields.fiscal_year ? String(dynamicFields.fiscal_year) : '');
        setBudgetAllocatedAmount(dynamicFields.allocated_amount ? String(dynamicFields.allocated_amount) : '');
        setBudgetUsedAmount(dynamicFields.used_amount ? String(dynamicFields.used_amount) : '');
      } else if (resource.resource_type === 'نیروی_انسانی') {
        setPersonCount(dynamicFields.person_count ? String(dynamicFields.person_count) : '');
        setPersonHours(dynamicFields.hours ? String(dynamicFields.hours) : '');
        setPersonSkillLevel(dynamicFields.skill_level || '');
        setPersonPosition(dynamicFields.position || '');
        setPersonDepartment(dynamicFields.department || '');
        setPersonAllocatedAmount(dynamicFields.allocated_amount ? String(dynamicFields.allocated_amount) : '');
        setPersonUsedAmount(dynamicFields.used_amount ? String(dynamicFields.used_amount) : '');
      } else if (resource.resource_type === 'تجهیزات') {
        setEquipmentName(dynamicFields.equipment_name || '');
        setEquipmentQuantity(dynamicFields.quantity ? String(dynamicFields.quantity) : '');
        setEquipmentModel(dynamicFields.model || '');
        setEquipmentSpecifications(dynamicFields.specifications || '');
        setEquipmentCondition(dynamicFields.condition || '');
        setEquipmentAllocatedAmount(dynamicFields.allocated_amount ? String(dynamicFields.allocated_amount) : '');
        setEquipmentUsedAmount(dynamicFields.used_amount ? String(dynamicFields.used_amount) : '');
      } else if (resource.resource_type === 'ابزار') {
        setToolName(dynamicFields.tool_name || '');
        setToolQuantity(dynamicFields.quantity ? String(dynamicFields.quantity) : '');
        setToolCondition(dynamicFields.condition || '');
        setToolSpecifications(dynamicFields.specifications || '');
        setToolAllocatedAmount(dynamicFields.allocated_amount ? String(dynamicFields.allocated_amount) : '');
        setToolUsedAmount(dynamicFields.used_amount ? String(dynamicFields.used_amount) : '');
      } else if (resource.resource_type === 'پیمانکار') {
        setContractorName(dynamicFields.contractor_name || '');
        setContractValue(dynamicFields.contract_value ? String(dynamicFields.contract_value) : '');
        setContractDuration(dynamicFields.contract_duration ? String(dynamicFields.contract_duration) : '');
        setContractDurationUnit(dynamicFields.contract_duration_unit || undefined);
        setContractType(dynamicFields.contract_type || '');
        setContractorAllocatedAmount(dynamicFields.allocated_amount ? String(dynamicFields.allocated_amount) : '');
        setContractorUsedAmount(dynamicFields.used_amount ? String(dynamicFields.used_amount) : '');
      } else if (resource.resource_type === 'سایر') {
        setCustomFields(Array.isArray(dynamicFields.custom_fields) ? dynamicFields.custom_fields : []);
        setOtherAllocatedAmount(dynamicFields.allocated_amount ? String(dynamicFields.allocated_amount) : '');
        setOtherUsedAmount(dynamicFields.used_amount ? String(dynamicFields.used_amount) : '');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در دریافت اطلاعات منبع آیتم');
    } finally {
      setResourceSubmitting(false);
    }
  };

  const onSubmitResource = async () => {
    if (!actionId || !resourceItemId) return;
    if (!resourceType) {
      message.error('نوع منبع الزامی است');
      return;
    }

    const dynamicFields = buildDynamicFields();
    if (dynamicFields === null) {
      return; // خطا در اعتبارسنجی
    }

    setResourceSubmitting(true);
    try {
      const payload = {
        resource_type: resourceType,
        dynamic_fields: dynamicFields,
      };
      
      if (resourceDescription) payload.description = resourceDescription;
      if (resourceStatus) payload.status = resourceStatus;

      if (editingResourceId) {
        const res = await actionApi.updateActionItemResource(
          actionId,
          resourceItemId,
          editingResourceId,
          payload
        );
        if (res?.success) {
          message.success(getSuccessMessage(res.message, 'منبع آیتم به‌روزرسانی شد'));
          resetAllResourceFieldsAndType();
          await loadItemResources(resourceItemId);
        } else {
          message.error(res?.message || 'خطا در به‌روزرسانی منبع آیتم');
        }
      } else {
        const res = await actionApi.createActionItemResource(actionId, resourceItemId, payload);
        if (res?.success) {
          message.success(getSuccessMessage(res.message, 'منبع آیتم ایجاد شد'));
          resetAllResourceFieldsAndType();
          await loadItemResources(resourceItemId);
        } else {
          message.error(res?.message || 'خطا در ایجاد منبع آیتم');
        }
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        message.error(error.message || 'خطا در اعتبارسنجی منبع آیتم');
      } else {
        message.error(error.message || 'خطا در ثبت منبع آیتم');
      }
    } finally {
      setResourceSubmitting(false);
    }
  };

  const onDeleteResource = async (resourceId) => {
    if (!actionId || !resourceItemId || !resourceId) return;
    try {
      const res = await actionApi.deleteActionItemResource(actionId, resourceItemId, resourceId);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'منبع آیتم حذف شد'));
        await loadItemResources(resourceItemId);
      } else {
        message.error(res?.message || 'خطا در حذف منبع آیتم');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در حذف منبع آیتم');
    }
  };

  // ---------- Attachments for items ----------
  const loadItemAttachments = async (itemId) => {
    if (!actionId || !itemId) return;
    setAttachmentsLoading(true);
    try {
      const res = await actionApi.getActionItemAttachments(actionId, itemId);
      const list = res?.data || res?.data?.data || [];
      setItemAttachments(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در دریافت پیوست‌های آیتم');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const openAttachmentModalForItem = async (itemId) => {
    if (!actionId || !itemId) return;
    setAttachmentItemId(itemId);
    setAttachmentModalOpen(true);
    setItemAttachments([]);
    setAttachmentFileList([]);
    setAttachmentDescription('');
    setEditingAttachmentId(null);
    setEditingAttachmentDescription('');
    await loadItemAttachments(itemId);
  };

  const closeAttachmentModal = () => {
    setAttachmentModalOpen(false);
    setAttachmentItemId(null);
    setItemAttachments([]);
    setAttachmentFileList([]);
    setAttachmentDescription('');
    setEditingAttachmentId(null);
    setEditingAttachmentDescription('');
  };

  const handleUploadAttachment = async () => {
    if (!actionId || !attachmentItemId) return;
    if (!attachmentFileList.length) {
      message.error('ابتدا یک فایل انتخاب کنید');
      return;
    }
    const file = attachmentFileList[0];
    setAttachmentUploading(true);
    try {
      const res = await actionApi.uploadActionItemAttachment(actionId, attachmentItemId, {
        file,
        description: attachmentDescription || undefined,
      });
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'فایل با موفقیت بارگذاری شد'));
        setAttachmentFileList([]);
        setAttachmentDescription('');
        await loadItemAttachments(attachmentItemId);
      } else {
        message.error(res?.message || 'خطا در بارگذاری فایل');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در بارگذاری فایل');
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const blob = await actionApi.downloadActionItemAttachment(
        actionId,
        attachmentItemId,
        attachmentId
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در دانلود فایل');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!actionId || !attachmentItemId || !attachmentId) return;
    try {
      const res = await actionApi.deleteActionItemAttachment(actionId, attachmentItemId, attachmentId);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'پیوست حذف شد'));
        await loadItemAttachments(attachmentItemId);
      } else {
        message.error(res?.message || 'خطا در حذف پیوست');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در حذف پیوست');
    }
  };

  const handleSaveAttachmentDescription = async () => {
    if (!actionId || !attachmentItemId || !editingAttachmentId) return;
    setEditingAttachmentLoading(true);
    try {
      const res = await actionApi.updateActionItemAttachmentDescription(
        actionId,
        attachmentItemId,
        editingAttachmentId,
        editingAttachmentDescription || null
      );
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'توضیحات پیوست به‌روزرسانی شد'));
        setEditingAttachmentId(null);
        setEditingAttachmentDescription('');
        await loadItemAttachments(attachmentItemId);
      } else {
        message.error(res?.message || 'خطا در به‌روزرسانی توضیحات');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در به‌روزرسانی توضیحات');
    } finally {
      setEditingAttachmentLoading(false);
    }
  };

  // ---------- Comments for items ----------
  const loadItemComments = async (itemId) => {
    if (!actionId || !itemId) return;
    setCommentsLoading(true);
    try {
      const res = await actionApi.getActionItemComments(actionId, itemId);
      // نمایش پاسخ با متن فارسی قابل خواندن
      console.log('💬 Comments Response (Parsed):', res);
      console.log('💬 Comments Response (Formatted):', JSON.stringify(res, null, 2));
      
      const list = res?.data || res?.data?.data || [];
      // فقط کامنت‌های اصلی (parent_id === null) را نگه می‌داریم
      const rootComments = Array.isArray(list)
        ? list.filter((c) => !c.parent_id || c.parent_id === null)
        : [];
      setItemComments(rootComments);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت نظرات');
    } finally {
      setCommentsLoading(false);
    }
  };

  const openCommentModalForItem = async (itemId) => {
    if (!actionId || !itemId) return;
    setCommentItemId(itemId);
    setCommentModalOpen(true);
    setItemComments([]);
    setCommentContent('');
    setCommentParentId(null);
    setMentionMappings([]);
    mentionRef.current?.clearMappings();
    await loadItemComments(itemId);
  };

  const closeCommentModal = () => {
    setCommentModalOpen(false);
    setCommentItemId(null);
    setItemComments([]);
    setCommentContent('');
    setCommentParentId(null);
    setMentionMappings([]);
    mentionRef.current?.clearMappings();
  };

  const onSubmitItemComment = async () => {
    if (!actionId || !commentItemId) return;
    
    // بررسی اینکه آیا sync در حال انجام است
    if (mentionRef.current?.isSyncing && mentionRef.current.isSyncing()) {
      message.warning('لطفاً صبر کنید تا همگام‌سازی کاربران منشن شده تکمیل شود...');
      return;
    }

    setCommentSubmitting(true);
    try {
      const rawContent = (commentContent || '').trim();
      if (!rawContent) {
        message.error('متن نظر الزامی است');
        return;
      }
      if (rawContent.length > 5000) {
        message.error('متن نظر حداکثر ۵۰۰۰ کاراکتر است');
        return;
      }

      // دریافت آخرین mappings از ref (این مطمئن‌ترین روش است)
      const currentMappings = mentionRef.current?.getMappings() || mentionMappings;
      
      console.log('=== Submit Comment Debug ===');
      console.log('Raw content:', rawContent);
      console.log('Mappings from ref:', mentionRef.current?.getMappings());
      console.log('Mappings from state:', mentionMappings);
      console.log('Using mappings:', currentMappings);

      // بررسی اینکه آیا همه mapping ها username دارند
      const mappingsWithoutUsername = currentMappings.filter(m => !m.username);
      if (mappingsWithoutUsername.length > 0) {
        console.warn('⚠️ Some mappings do not have username:', mappingsWithoutUsername);
      }

      // تبدیل منشن‌ها از @نام به @username برای ارسال به بک‌اند
      const content = convertMentionsToEmails(rawContent, currentMappings);
      
      console.log('=== Conversion Result ===');
      console.log('Before:', rawContent);
      console.log('After:', content);

      const payload = { content };
      if (commentParentId) payload.parent_id = commentParentId;
      
      console.log('Final payload:', payload);
      
      const res = await actionApi.createActionItemComment(actionId, commentItemId, payload);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'نظر با موفقیت ثبت شد'));
        setCommentContent('');
        setCommentParentId(null);
        setMentionMappings([]);
        // پاک کردن mappings از ref
        mentionRef.current?.clearMappings();
        await loadItemComments(commentItemId);
      } else {
        message.error(res?.message || 'خطا در ثبت نظر');
      }
    } catch (error) {
      console.error(error);
      if (error instanceof ActionValidationError) {
        const contentErr = error.errors?.content?.[0];
        const parentErr = error.errors?.parent_id?.[0];
        message.error(contentErr || parentErr || error.message || 'خطا در اعتبارسنجی نظر');
      } else {
        message.error(error.message || 'خطا در ثبت نظر');
      }
    } finally {
      setCommentSubmitting(false);
    }
  };

  const onDeleteItemComment = async (commentId) => {
    if (!actionId || !commentItemId || !commentId) return;
    setCommentDeletingId(commentId);
    try {
      const res = await actionApi.deleteActionItemComment(actionId, commentItemId, commentId);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'نظر حذف شد'));
        await loadItemComments(commentItemId);
      } else {
        message.error(res?.message || 'خطا در حذف نظر');
      }
    } catch (error) {
      console.error(error);
      message.error(error.message || 'خطا در حذف نظر');
    } finally {
      setCommentDeletingId(null);
    }
  };

  // کامپوننت recursive برای نمایش replies
  const CommentReplies = ({ replies, depth = 0 }) => {
    if (!replies || !Array.isArray(replies) || replies.length === 0) {
      return null;
    }

    const indentLevel = depth * 24;

    return (
      <div
        className="mt-2 space-y-2"
        style={{
          marginLeft: `${indentLevel}px`,
          paddingLeft: '16px',
          borderLeft: '2px solid #d9d9d9',
        }}
      >
        {replies.map((reply) => (
          <div key={reply.id} className="flex flex-col">
            <div
              className={`p-3 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col gap-2 ${
                depth === 0
                  ? 'bg-gray-50 dark:bg-gray-800/50'
                  : depth === 1
                  ? 'bg-gray-100 dark:bg-gray-800/30'
                  : 'bg-gray-200 dark:bg-gray-800/20'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Avatar
                    size="small"
                    style={{
                      backgroundColor:
                        depth === 0 ? '#52c41a' : depth === 1 ? '#faad14' : '#ff7875',
                      fontSize: '11px',
                    }}
                  >
                    {String(reply.user?.name || '').slice(0, 1).toUpperCase() || '?'}
                  </Avatar>
                  <div className="flex items-center gap-1.5">
                    <ArrowLeftOutlined style={{ fontSize: '11px', color: '#8c8c8c' }} />
                    <div className="flex flex-col">
                      <Text style={{ fontFamily: 'CustomFont', fontSize: '12px', fontWeight: 500 }}>
                        {reply.user?.name || 'کاربر ناشناس'}
                      </Text>
                      {reply.created_at && (
                        <Text
                          type="secondary"
                          style={{ fontFamily: 'CustomFont', fontSize: 10 }}
                        >
                          {new Date(reply.created_at).toLocaleString('fa-IR')}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
                {reply.is_edited && (
                  <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: 9 }}>
                    ویرایش شده
                  </Tag>
                )}
              </div>
              <Text
                style={{
                  fontFamily: 'CustomFont',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  color: '#595959',
                }}
              >
                {renderContentWithMentions(reply.content)}
              </Text>
              <div className="mt-1 flex justify-end gap-2">
                <Button
                  type="link"
                  size="small"
                  onClick={() => setCommentParentId(reply.id)}
                  style={{ fontFamily: 'CustomFont', padding: '0 6px', fontSize: '11px' }}
                >
                  پاسخ
                </Button>
                <Popconfirm
                  title="حذف پاسخ"
                  description="آیا از حذف این پاسخ مطمئن هستید؟"
                  okText="حذف"
                  cancelText="انصراف"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDeleteItemComment(reply.id)}
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    loading={commentDeletingId === reply.id}
                    style={{ fontFamily: 'CustomFont', padding: '0 6px', fontSize: '11px' }}
                  >
                    حذف
                  </Button>
                </Popconfirm>
              </div>
            </div>

            {reply.replies && Array.isArray(reply.replies) && reply.replies.length > 0 && (
              <CommentReplies replies={reply.replies} depth={depth + 1} />
            )}
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId]);

  return (
    <>
      <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 16 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <OrderedListOutlined style={{ color: '#4b5563' }} />
              <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                آیتم‌های اقدام
              </Title>
            </div>
            {/* <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={openCreateForm}
              className={primaryButtonClass}
              style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
            >
              آیتم جدید
            </Button> */}
          </div>

          {loading ? (
            <div className="py-8">
              <CardListSkeleton count={4} />
            </div>
          ) : items.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ fontFamily: 'CustomFont' }}>هنوز آیتمی ثبت نشده است</span>}
            />
          ) : (
            <div className="space-y-3">
              {items.map((item) => {
                const descriptionMaxLength = 100;
                const isExpanded = expandedDescriptions[item.id] || false;
                const shouldTruncate = item.description && item.description.length > descriptionMaxLength;
                const displayDescription = shouldTruncate && !isExpanded
                  ? item.description.substring(0, descriptionMaxLength) + '...'
                  : item.description;
                
                const toggleDescription = () => {
                  setExpandedDescriptions(prev => ({
                    ...prev,
                    [item.id]: !prev[item.id]
                  }));
                };

                return (
                  <div
                    key={item.id}
                    className={`p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col gap-3 ${
                      editingId === item.id ? 'ring-2 ring-emerald-400' : ''
                    }`}
                  >
                    {/* ردیف اول: چک‌باکس، عنوان، و دکمه‌ها */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="flex flex-col items-center gap-2 pt-1">
                          <Checkbox
                            checked={!!item.actual_end_date}
                            disabled={!!item.actual_end_date || completingId === item.id}
                            onChange={() => handleToggleComplete(item)}
                          />
                          {item.can_start && (
                            <Tooltip title="شروع آیتم">
                              <Button
                                type="primary"
                                size="small"
                                icon={<PlayCircleOutlined />}
                                loading={startingId === item.id}
                                onClick={() => handleStart(item.id)}
                                style={{
                                  fontFamily: 'CustomFont',
                                  background: '#10b981',
                                  borderColor: '#10b981',
                                  borderRadius: '50%',
                                  width: 32,
                                  height: 32,
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont', fontSize: 15 }}>
                              {item.title}
                            </Title>
                            {item.parent_item_id && (
                              <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                                زیرمجموعه #{item.parent_item_id}
                              </Tag>
                            )}
                            {item.priority && mapPriorityTag(item.priority)}
                            {item.status && mapStatusTag(item.status)}
                          </div>
                          
                          {/* توضیحات */}
                          {item.description && (
                            <div className="mt-2">
                              <Text
                                style={{
                                  fontFamily: 'CustomFont',
                                  fontSize: 13,
                                  color: '#6b7280',
                                  lineHeight: '1.6',
                                  display: 'block',
                                }}
                              >
                                {displayDescription}
                              </Text>
                              {shouldTruncate && (
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={toggleDescription}
                                  style={{
                                    fontFamily: 'CustomFont',
                                    padding: '4px 0',
                                    height: 'auto',
                                    fontSize: 12,
                                  }}
                                >
                                  {isExpanded ? (
                                    <>
                                      <UpOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                                      بستن
                                    </>
                                  ) : (
                                    <>
                                      <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                                      مشاهده بیشتر
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          )}

                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Tooltip title="مدیریت تیم RACI">
                          {item.racis_count > 0 ? (
                            <Badge
                              count={item.racis_count}
                              size="small"
                              offset={[-2.5, 2.5]}
                              style={{ backgroundColor: '#3b82f6' }}
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={<TeamOutlined />}
                                onClick={() => openRaciModalForItem(item.id)}
                                style={{ fontFamily: 'CustomFont' }}
                              />
                            </Badge>
                          ) : (
                            <Button
                              type="text"
                              size="small"
                              icon={<TeamOutlined />}
                              onClick={() => openRaciModalForItem(item.id)}
                              style={{ fontFamily: 'CustomFont' }}
                            />
                          )}
                        </Tooltip>
                        <Tooltip title="مدیریت منابع">
                          {item.resources_count > 0 ? (
                            <Badge
                              count={item.resources_count}
                              size="small"
                              offset={[-2, 2]}
                              style={{ backgroundColor: '#f59e0b' }}
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={<ToolOutlined />}
                                onClick={() => openResourceModalForItem(item.id)}
                                style={{ fontFamily: 'CustomFont' }}
                              />
                            </Badge>
                          ) : (
                            <Button
                              type="text"
                              size="small"
                              icon={<ToolOutlined />}
                              onClick={() => openResourceModalForItem(item.id)}
                              style={{ fontFamily: 'CustomFont' }}
                            />
                          )}
                        </Tooltip>
                        <Tooltip title="پیوست‌ها">
                          {item.attachments_count > 0 ? (
                            <Badge
                              count={item.attachments_count}
                              size="small"
                              offset={[-2, 2]}
                              style={{ backgroundColor: '#8b5cf6' }}
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={<PaperClipOutlined />}
                                onClick={() => openAttachmentModalForItem(item.id)}
                                style={{ fontFamily: 'CustomFont' }}
                              />
                            </Badge>
                          ) : (
                            <Button
                              type="text"
                              size="small"
                              icon={<PaperClipOutlined />}
                              onClick={() => openAttachmentModalForItem(item.id)}
                              style={{ fontFamily: 'CustomFont' }}
                            />
                          )}
                        </Tooltip>
                        <Tooltip title="نظرات">
                          <Button
                            type="text"
                            size="small"
                            icon={<MessageOutlined />}
                            onClick={() => openCommentModalForItem(item.id)}
                            style={{ fontFamily: 'CustomFont' }}
                          />
                        </Tooltip>
                        <Tooltip title="ویرایش آیتم">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEditForm(item.id)}
                            style={{ fontFamily: 'CustomFont' }}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="حذف آیتم"
                          description="آیا از حذف این آیتم مطمئن هستید؟"
                          okText="حذف"
                          cancelText="انصراف"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => handleDelete(item.id)}
                        >
                          <Tooltip title="حذف آیتم">
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              style={{ fontFamily: 'CustomFont' }}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </div>
                    </div>

                    {/* ردیف دوم: تاریخ‌ها */}
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {/* تاریخ‌های برنامه‌ریزی */}
                        {(item.planned_start_date || item.planned_end_date) && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              <CalendarOutlined className="text-[11px] text-blue-500" />
                              <Text
                                type="secondary"
                                style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                              >
                                برنامه‌ریزی:
                              </Text>
                            </div>
                            <div className="flex flex-col gap-0.5 pr-5">
                              {item.planned_start_date && (
                                <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                                  شروع: {item.planned_start_date}
                                </Text>
                              )}
                              {item.planned_end_date && (
                                <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                                  پایان: {item.planned_end_date}
                                </Text>
                              )}
                            </div>
                          </div>
                        )}

                        {/* تاریخ‌های واقعی */}
                        {(item.actual_start_date || item.actual_end_date) && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              <CalendarOutlined className="text-[11px] text-emerald-500" />
                              <Text
                                type="secondary"
                                style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                              >
                                واقعی:
                              </Text>
                            </div>
                            <div className="flex flex-col gap-0.5 pr-5">
                              {item.actual_start_date && (
                                <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                                  شروع: {item.actual_start_date}
                                </Text>
                              )}
                              {item.actual_end_date && (
                                <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                                  پایان: {item.actual_end_date}
                                </Text>
                              )}
                            </div>
                          </div>
                        )}

                        {/* تاریخ ایجاد و ویرایش */}
                        {(item.created_at || item.updated_at) && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              <ClockCircleOutlined className="text-[11px] text-gray-500" />
                              <Text
                                type="secondary"
                                style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                              >
                                اطلاعات:
                              </Text>
                            </div>
                            <div className="flex flex-col gap-0.5 pr-5">
                              {item.created_at && (
                                <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                                  ایجاد: {item.created_at}
                                </Text>
                              )}
                              {item.updated_at && (
                                <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                                  ویرایش: {item.updated_at}
                                </Text>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ردیف سوم: اعضای RACI */}
                    {item.racis && item.racis.length > 0 && (
                      <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2 mb-2">
                          <TeamOutlined className="text-[12px] text-blue-500" />
                          <Text
                            type="secondary"
                            style={{ fontFamily: 'CustomFont', fontSize: 11, fontWeight: 500 }}
                          >
                            تیم RACI:
                          </Text>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {item.racis.map((raci) => {
                            const roleColors = {
                              'پاسخگو': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
                              'مسئول_اجرا': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
                              'مطلع': { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
                              'مشاور': { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
                            };
                            const roleColor = roleColors[raci.raci_role] || { bg: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
                            
                            return (
                              <Tooltip
                                key={raci.id}
                                title={
                                  <div style={{ fontFamily: 'CustomFont' }}>
                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{raci.user?.name || 'نامشخص'}</div>
                                    <div style={{ fontSize: 11 }}>نقش: {raci.raci_role}</div>
                                    {raci.user?.email && (
                                      <div style={{ fontSize: 11, opacity: 0.8 }}>{raci.user.email}</div>
                                    )}
                                  </div>
                                }
                              >
                                <div
                                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border ${roleColor.bg} ${roleColor.border} ${roleColor.text} transition-all hover:shadow-sm cursor-default`}
                                >
                                  <Avatar
                                    size={20}
                                    src={raci.user?.profile_img}
                                    style={{ flexShrink: 0 }}
                                  >
                                    {raci.user?.name ? String(raci.user.name).charAt(0) : '?'}
                                  </Avatar>
                                  <Text
                                    style={{
                                      fontFamily: 'CustomFont',
                                      fontSize: 11,
                                      fontWeight: 500,
                                      maxWidth: 100,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {raci.user?.name || 'نامشخص'}
                                  </Text>
                                  <Tag
                                    size="small"
                                    style={{
                                      fontFamily: 'CustomFont',
                                      fontSize: 9,
                                      margin: 0,
                                      padding: '0 6px',
                                      height: 18,
                                      lineHeight: '18px',
                                      borderRadius: 4,
                                    }}
                                    className={roleColor.bg}
                                  >
                                    {raci.raci_role}
                                  </Tag>
                                </div>
                              </Tooltip>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Col>

        <Col xs={24} lg={10}>
          <Card size="small" style={{ borderRadius: 16, background: '#f9fafb' }} bodyStyle={{ padding: 16 }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
                  {editingId ? 'ویرایش آیتم اقدام' : 'ایجاد آیتم جدید'}
                </Title>
                <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                  تقسیم اقدام به آیتم‌های کوچکتر برای مدیریت بهتر
                </Text>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Text style={{ fontFamily: 'CustomFont' }} strong>
                  عنوان آیتم
                </Text>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={255}
                  style={{ marginTop: 4, borderRadius: 10 }}
                />
              </div>

              <div>
                <Text style={{ fontFamily: 'CustomFont' }}>توضیحات (اختیاری)</Text>
                <TextArea
                  rows={3}
                  maxLength={2000}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{ marginTop: 4, borderRadius: 10 }}
                />
              </div>

              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>اولویت</Text>
                  <Select
                    size="middle"
                    placeholder="انتخاب کنید"
                    value={priority}
                    onChange={(val) => setPriority(val)}
                    style={{ width: '100%', marginTop: 4 }}
                  >
                    {PRIORITY_OPTIONS.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        <Tag color={opt.color} style={{ fontFamily: 'CustomFont', margin: 0 }}>
                          {opt.label}
                        </Tag>
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>وضعیت</Text>
                  <Select
                    size="middle"
                    placeholder="انتخاب کنید"
                    value={status}
                    onChange={(val) => setStatus(val)}
                    style={{ width: '100%', marginTop: 4 }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        <Tag color={opt.color} style={{ fontFamily: 'CustomFont', margin: 0 }}>
                          {opt.label}
                        </Tag>
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              <div>
                <Text style={{ fontFamily: 'CustomFont' }}>درصد پیشرفت</Text>
                <div className="flex items-center gap-3 mt-2">
                  <Slider
                    min={0}
                    max={100}
                    value={percentComplete}
                    onChange={(val) => setPercentComplete(val)}
                    style={{ flex: 1 }}
                  />
                  <InputNumber
                    min={0}
                    max={100}
                    precision={0}
                    value={percentComplete}
                    onChange={(val) => setPercentComplete(val || 0)}
                    style={{ width: 70 }}
                  />
                  <span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>%</span>
                </div>
              </div>

              <div>
                <Text style={{ fontFamily: 'CustomFont' }}>وزن (۰ تا ۱۰۰)</Text>
                <InputNumber
                  min={0}
                  max={100}
                  precision={0}
                  value={weight}
                  onChange={(val) => setWeight(val)}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </div>

              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>تاریخ شروع برنامه‌ریزی</Text>
                  <PersianDatePicker
                    value={plannedStart}
                    onChange={(val) => setPlannedStart(val)}
                    placeholder="انتخاب تاریخ و ساعت"
                    showTime={true}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </Col>
                <Col span={12}>
                  <Text style={{ fontFamily: 'CustomFont' }}>تاریخ پایان برنامه‌ریزی</Text>
                  <PersianDatePicker
                    value={plannedEnd}
                    onChange={(val) => setPlannedEnd(val)}
                    placeholder="انتخاب تاریخ و ساعت"
                    showTime={true}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </Col>
              </Row>

              <div className="flex justify-end gap-2 pt-2">
                {editingId && (
                  <Button
                    onClick={resetForm}
                    style={{ borderRadius: 12, fontFamily: 'CustomFont' }}
                    disabled={formLoading}
                  >
                    لغو و ساخت آیتم جدید
                  </Button>
                )}
                <Button
                  type="primary"
                  className={primaryButtonClass}
                  loading={formLoading}
                  style={{ borderRadius: 12, fontFamily: 'CustomFont' }}
                  onClick={handleSubmit}
                >
                  {editingId ? 'به‌روزرسانی آیتم' : 'ثبت آیتم'}
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </Card>

      <Modal
        open={raciModalOpen}
        onCancel={closeRaciModal}
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            مدیریت تیم آیتم
          </span>
        }
        footer={null}
        destroyOnClose
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont', fontSize: 14 }}>
                اعضای RACI آیتم
              </Title>
              <Badge count={itemRacis.length} />
            </div>
          </div>

          {raciLoading && itemRacis.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Spin />
            </div>
          ) : itemRacis.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ fontFamily: 'CustomFont' }}>هنوز عضوی ثبت نشده است</span>}
            />
          ) : (
            <div className="space-y-2 max-h-64 overflow-auto">
              {itemRacis.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Avatar size="small">
                      {String(r.user?.name || '').slice(0, 1) || '?'}
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div
                        className="truncate"
                        style={{ fontFamily: 'CustomFont', fontSize: 13 }}
                      >
                        {r.user?.name || '---'}
                      </div>
                      <div
                        className="truncate text-xs text-gray-500"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {r.user?.email || ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Tag style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                      {r.raci_role}
                    </Tag>
                    {r.created_at && (
                      <Text 
                        type="secondary" 
                        style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                        className="flex items-center gap-1"
                      >
                        <CalendarOutlined className="text-[10px] text-gray-400 dark:text-gray-500" />
                        {(r.created_at)}
                      </Text>
                    )}
                    <Popconfirm
                      title="حذف عضو"
                      description="آیا از حذف این عضو مطمئن هستید؟"
                      okText="حذف"
                      cancelText="انصراف"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDeleteItemRaci(r.id)}
                    >
                      <Button type="text" size="small" danger>
                        <DeleteOutlined />
                      </Button>
                    </Popconfirm>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="pt-3 border-t border-gray-100 dark:border-gray-700 space-y-3">
            <Text style={{ fontFamily: 'CustomFont', fontSize: 13 }}>
              افزودن عضو جدید
            </Text>
            <Select
              showSearch
              size="middle"
              placeholder="انتخاب کاربر"
              value={selectedErpUserId}
              onChange={async (val) => {
                setSelectedErpUserId(val);
                setSelectedActionUserId(undefined);
                if (!val) return;
                try {
                  message.loading({
                    content: 'در حال همگام‌سازی کاربر...',
                    key: 'sync-user-item-raci',
                  });
                  const actionUserId = await syncUserAndGetActionUserId(val);
                  setSelectedActionUserId(actionUserId);
                  message.success({
                    content: 'کاربر با موفقیت همگام‌سازی شد',
                    key: 'sync-user-item-raci',
                    duration: 2,
                  });
                } catch (e) {
                  setSelectedErpUserId(undefined);
                  message.error({
                    content: e.message || 'خطا در همگام‌سازی کاربر',
                    key: 'sync-user-item-raci',
                  });
                }
              }}
              onSearch={(val) => fetchErpEmployees(val)}
              loading={erpLoading}
              filterOption={false}
              style={{ width: '100%' }}
            >
              {erpEmployees.map((u) => (
                <Select.Option key={u.id} value={u.id}>
                  <Space>
                    <Avatar size="small">
                      {String(u.name || '').slice(0, 1) || '?'}
                    </Avatar>
                    <div className="leading-tight">
                      <div style={{ fontFamily: 'CustomFont' }}>{u.name}</div>
                      <div
                        className="text-xs text-gray-500"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {u.email}
                      </div>
                    </div>
                  </Space>
                </Select.Option>
              ))}
            </Select>

            <Select
              size="middle"
              placeholder="انتخاب نقش RACI"
              value={selectedRaciRole}
              onChange={(val) => setSelectedRaciRole(val)}
              style={{ width: '100%' }}
            >
              <Select.Option value="مسئول_اجرا">مسئول اجرا</Select.Option>
              <Select.Option value="پاسخگو">پاسخگو</Select.Option>
              <Select.Option value="مشاور">مشاور</Select.Option>
              <Select.Option value="مطلع">مطلع</Select.Option>
            </Select>

            <div className="flex justify-end">
              <Button
                type="primary"
                className={primaryButtonClass}
                loading={raciLoading}
                onClick={handleAddItemRaci}
                style={{ borderRadius: 12, fontFamily: 'CustomFont' }}
              >
                افزودن عضو
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={resourceModalOpen}
        onCancel={closeResourceModal}
        width={800}
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            مدیریت منابع آیتم
          </span>
        }
        footer={null}
        destroyOnClose
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont', fontSize: 14 }}>
                لیست منابع آیتم
              </Title>
              <Badge count={itemResources.length} />
            </div>
          </div>

          {resourcesLoading && itemResources.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Spin />
            </div>
          ) : itemResources.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ fontFamily: 'CustomFont' }}>هنوز منبعی ثبت نشده است</span>}
            />
          ) : (
            <div className="space-y-2 max-h-64 overflow-auto">
              {itemResources.map((res) => {
                const dynamicFields = res.dynamic_fields || {};
                const config = getResourceTypeConfig(res.resource_type);
                
                // نمایش اطلاعات بر اساس نوع منبع
                let displayInfo = '';
                if (res.resource_type === 'بودجه') {
                  displayInfo = `${dynamicFields.amount?.toLocaleString('fa-IR') || 0} ${dynamicFields.currency || ''}`;
                } else if (res.resource_type === 'نیروی_انسانی') {
                  displayInfo = `${dynamicFields.person_count || 0} نفر${dynamicFields.hours ? ` - ${dynamicFields.hours} ساعت` : ''}`;
                } else if (res.resource_type === 'تجهیزات') {
                  displayInfo = `${dynamicFields.equipment_name || ''} (${dynamicFields.quantity || 0} عدد)`;
                } else if (res.resource_type === 'ابزار') {
                  displayInfo = `${dynamicFields.tool_name || ''} (${dynamicFields.quantity || 0} عدد)`;
                } else if (res.resource_type === 'پیمانکار') {
                  displayInfo = `${dynamicFields.contractor_name || ''} - ${dynamicFields.contract_value?.toLocaleString('fa-IR') || 0}`;
                } else if (res.resource_type === 'سایر') {
                  displayInfo = res.description || 'منبع سفارشی';
                }
                
                return (
                  <div
                    key={res.id}
                    className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{
                          background: config.bgColor,
                          color: config.color,
                        }}
                      >
                        {config.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Text
                            strong
                            style={{ fontFamily: 'CustomFont', fontSize: 14 }}
                          >
                            {res.resource_type}
                          </Text>
                          {res.status && (
                            <Tag 
                              color={
                                res.status === 'completed' ? 'success' :
                                res.status === 'used' ? 'processing' :
                                res.status === 'allocated' ? 'warning' : 'default'
                              }
                              style={{ fontFamily: 'CustomFont', fontSize: 10 }}
                            >
                              {res.status === 'planned' ? 'برنامه‌ریزی شده' :
                               res.status === 'allocated' ? 'تخصیص داده شده' :
                               res.status === 'used' ? 'استفاده شده' :
                               res.status === 'completed' ? 'تکمیل شده' : res.status}
                            </Tag>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Text
                            type="secondary"
                            style={{ fontFamily: 'CustomFont', fontSize: 13 }}
                          >
                            {displayInfo}
                          </Text>
                          {res.description && (
                            <>
                              <span className="text-gray-300 dark:text-gray-600">•</span>
                              <Text
                                type="secondary"
                                className="truncate"
                                style={{ fontFamily: 'CustomFont', fontSize: 12 }}
                              >
                                {res.description}
                              </Text>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEditResource(res.id)}
                        style={{ fontFamily: 'CustomFont' }}
                      />
                      <Popconfirm
                        title="حذف منبع"
                        description="آیا از حذف این منبع مطمئن هستید؟"
                        okText="حذف"
                        cancelText="انصراف"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDeleteResource(res.id)}
                      >
                        <Button type="text" size="small" danger>
                          <DeleteOutlined />
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pt-4 border-t border-gray-100 dark:border-gray-700 space-y-3">
            <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont', fontSize: 14 }}>
              {editingResourceId ? 'ویرایش منبع آیتم' : 'ایجاد منبع جدید برای آیتم'}
            </Title>

            <div>
              <Text style={{ fontFamily: 'CustomFont' }} strong>نوع منبع (الزامی)</Text>
              <Select
                size="middle"
                placeholder="انتخاب نوع منبع"
                value={resourceType}
                onChange={(val) => {
                  setResourceType(val);
                  resetAllResourceFields();
                }}
                style={{ width: '100%', marginTop: 4 }}
              >
                <Option value="بودجه">بودجه</Option>
                <Option value="ابزار">ابزار</Option>
                <Option value="نیروی_انسانی">نیروی انسانی</Option>
                <Option value="تجهیزات">تجهیزات</Option>
                <Option value="پیمانکار">پیمانکار</Option>
                <Option value="سایر">سایر</Option>
              </Select>
            </div>

            {/* فیلدهای پویا بر اساس نوع منبع */}
            {resourceType === 'بودجه' && (
              <>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }} strong>مبلغ بودجه (الزامی)</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={budgetAmount}
                      onChange={(v) => setBudgetAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="مثلاً: 1000000"
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }} strong>واحد پول (الزامی)</Text>
                    <Input
                      size="middle"
                      maxLength={50}
                      value={budgetCurrency}
                      onChange={(e) => setBudgetCurrency(e.target.value)}
                      placeholder="مثلاً: ریال"
                      style={{ marginTop: 4 }}
                    />
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>دسته‌بندی بودجه</Text>
                    <Input
                      size="middle"
                      maxLength={255}
                      value={budgetCategory}
                      onChange={(e) => setBudgetCategory(e.target.value)}
                      placeholder="مثلاً: عملیاتی"
                      style={{ marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>سال مالی</Text>
                    <InputNumber
                      size="middle"
                      min={1400}
                      max={1500}
                      precision={0}
                      value={budgetFiscalYear}
                      onChange={(v) => setBudgetFiscalYear(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="مثلاً: 1403"
                    />
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار تخصیص داده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={budgetAllocatedAmount}
                      onChange={(v) => setBudgetAllocatedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار استفاده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={budgetUsedAmount}
                      onChange={(v) => setBudgetUsedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                </Row>
              </>
            )}

            {resourceType === 'نیروی_انسانی' && (
              <>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }} strong>تعداد نفرات (الزامی)</Text>
                    <InputNumber
                      size="middle"
                      min={1}
                      precision={0}
                      value={personCount}
                      onChange={(v) => setPersonCount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="مثلاً: 5"
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>تعداد ساعت</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      precision={0}
                      value={personHours}
                      onChange={(v) => setPersonHours(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="مثلاً: 160"
                    />
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>سطح مهارت</Text>
                    <Input
                      size="middle"
                      maxLength={100}
                      value={personSkillLevel}
                      onChange={(e) => setPersonSkillLevel(e.target.value)}
                      placeholder="مثلاً: متوسط"
                      style={{ marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>موقعیت شغلی</Text>
                    <Input
                      size="middle"
                      maxLength={255}
                      value={personPosition}
                      onChange={(e) => setPersonPosition(e.target.value)}
                      placeholder="مثلاً: برنامه‌نویس"
                      style={{ marginTop: 4 }}
                    />
                  </Col>
                </Row>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }}>دپارتمان</Text>
                  <Input
                    size="middle"
                    maxLength={255}
                    value={personDepartment}
                    onChange={(e) => setPersonDepartment(e.target.value)}
                    placeholder="مثلاً: IT"
                    style={{ marginTop: 4 }}
                  />
                </div>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار تخصیص داده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={personAllocatedAmount}
                      onChange={(v) => setPersonAllocatedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار استفاده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={personUsedAmount}
                      onChange={(v) => setPersonUsedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                </Row>
              </>
            )}

            {resourceType === 'تجهیزات' && (
              <>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }} strong>نام تجهیزات (الزامی)</Text>
                  <Input
                    size="middle"
                    maxLength={255}
                    value={equipmentName}
                    onChange={(e) => setEquipmentName(e.target.value)}
                    placeholder="مثلاً: سرور HP ProLiant"
                    style={{ marginTop: 4 }}
                  />
                </div>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }} strong>تعداد (الزامی)</Text>
                    <InputNumber
                      size="middle"
                      min={1}
                      precision={0}
                      value={equipmentQuantity}
                      onChange={(v) => setEquipmentQuantity(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="مثلاً: 2"
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مدل</Text>
                    <Input
                      size="middle"
                      maxLength={255}
                      value={equipmentModel}
                      onChange={(e) => setEquipmentModel(e.target.value)}
                      placeholder="مثلاً: DL380 Gen10"
                      style={{ marginTop: 4 }}
                    />
                  </Col>
                </Row>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }}>مشخصات</Text>
                  <TextArea
                    rows={3}
                    maxLength={65535}
                    value={equipmentSpecifications}
                    onChange={(e) => setEquipmentSpecifications(e.target.value)}
                    placeholder="مثلاً: 32GB RAM, 2TB SSD"
                    style={{ marginTop: 4, borderRadius: 10 }}
                  />
                </div>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }}>وضعیت</Text>
                  <Input
                    size="middle"
                    maxLength={100}
                    value={equipmentCondition}
                    onChange={(e) => setEquipmentCondition(e.target.value)}
                    placeholder="مثلاً: نو"
                    style={{ marginTop: 4 }}
                  />
                </div>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار تخصیص داده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={equipmentAllocatedAmount}
                      onChange={(v) => setEquipmentAllocatedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار استفاده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={equipmentUsedAmount}
                      onChange={(v) => setEquipmentUsedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                </Row>
              </>
            )}

            {resourceType === 'ابزار' && (
              <>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }} strong>نام ابزار (الزامی)</Text>
                  <Input
                    size="middle"
                    maxLength={255}
                    value={toolName}
                    onChange={(e) => setToolName(e.target.value)}
                    placeholder="مثلاً: لپ‌تاپ"
                    style={{ marginTop: 4 }}
                  />
                </div>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }} strong>تعداد (الزامی)</Text>
                    <InputNumber
                      size="middle"
                      min={1}
                      precision={0}
                      value={toolQuantity}
                      onChange={(v) => setToolQuantity(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="مثلاً: 3"
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>وضعیت</Text>
                    <Input
                      size="middle"
                      maxLength={100}
                      value={toolCondition}
                      onChange={(e) => setToolCondition(e.target.value)}
                      placeholder="مثلاً: خوب"
                      style={{ marginTop: 4 }}
                    />
                  </Col>
                </Row>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }}>مشخصات</Text>
                  <TextArea
                    rows={3}
                    maxLength={65535}
                    value={toolSpecifications}
                    onChange={(e) => setToolSpecifications(e.target.value)}
                    placeholder="مثلاً: Intel i7, 16GB RAM"
                    style={{ marginTop: 4, borderRadius: 10 }}
                  />
                </div>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار تخصیص داده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={toolAllocatedAmount}
                      onChange={(v) => setToolAllocatedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار استفاده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={toolUsedAmount}
                      onChange={(v) => setToolUsedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                </Row>
              </>
            )}

            {resourceType === 'پیمانکار' && (
              <>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }} strong>نام پیمانکار (الزامی)</Text>
                  <Input
                    size="middle"
                    maxLength={255}
                    value={contractorName}
                    onChange={(e) => setContractorName(e.target.value)}
                    placeholder="مثلاً: شرکت ABC"
                    style={{ marginTop: 4 }}
                  />
                </div>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }} strong>مبلغ قرارداد (الزامی)</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={contractValue}
                      onChange={(v) => setContractValue(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="مثلاً: 5000000"
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>نوع قرارداد</Text>
                    <Input
                      size="middle"
                      maxLength={255}
                      value={contractType}
                      onChange={(e) => setContractType(e.target.value)}
                      placeholder="مثلاً: ثابت"
                      style={{ marginTop: 4 }}
                    />
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} sm={8}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مدت قرارداد</Text>
                    <InputNumber
                      size="middle"
                      min={1}
                      precision={0}
                      value={contractDuration}
                      onChange={(v) => setContractDuration(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="مثلاً: 6"
                    />
                  </Col>
                  <Col xs={24} sm={16}>
                    <Text style={{ fontFamily: 'CustomFont' }}>واحد مدت قرارداد</Text>
                    <Select
                      size="middle"
                      placeholder="انتخاب واحد"
                      value={contractDurationUnit}
                      onChange={setContractDurationUnit}
                      style={{ width: '100%', marginTop: 4 }}
                    >
                      <Option value="روز">روز</Option>
                      <Option value="ماه">ماه</Option>
                      <Option value="سال">سال</Option>
                    </Select>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار تخصیص داده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={contractorAllocatedAmount}
                      onChange={(v) => setContractorAllocatedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار استفاده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={contractorUsedAmount}
                      onChange={(v) => setContractorUsedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                </Row>
              </>
            )}

            {resourceType === 'سایر' && (
              <>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }}>فیلدهای سفارشی</Text>
                  <div className="space-y-2 mt-2">
                    {customFields.map((cf, index) => (
                      <Row key={index} gutter={8}>
                        <Col xs={24} sm={10}>
                          <Input
                            size="middle"
                            maxLength={255}
                            value={cf.key || ''}
                            onChange={(e) => {
                              const newFields = [...customFields];
                              newFields[index] = { ...newFields[index], key: e.target.value };
                              setCustomFields(newFields);
                            }}
                            placeholder="کلید"
                          />
                        </Col>
                        <Col xs={24} sm={12}>
                          <Input
                            size="middle"
                            value={cf.value || ''}
                            onChange={(e) => {
                              const newFields = [...customFields];
                              newFields[index] = { ...newFields[index], value: e.target.value };
                              setCustomFields(newFields);
                            }}
                            placeholder="مقدار"
                          />
                        </Col>
                        <Col xs={24} sm={2}>
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              setCustomFields(customFields.filter((_, i) => i !== index));
                            }}
                            style={{ width: '100%' }}
                          />
                        </Col>
                      </Row>
                    ))}
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => setCustomFields([...customFields, { key: '', value: '' }])}
                      style={{ width: '100%', borderRadius: 10 }}
                    >
                      افزودن فیلد سفارشی
                    </Button>
                  </div>
                </div>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار تخصیص داده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={otherAllocatedAmount}
                      onChange={(v) => setOtherAllocatedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text style={{ fontFamily: 'CustomFont' }}>مقدار استفاده شده</Text>
                    <InputNumber
                      size="middle"
                      min={0}
                      max={999999999999}
                      precision={0}
                      formatter={numberFormatter}
                      parser={numberParser}
                      value={otherUsedAmount}
                      onChange={(v) => setOtherUsedAmount(v || '')}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </Col>
                </Row>
              </>
            )}

            {/* فیلدهای مشترک */}
            {resourceType && (
              <>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }}>وضعیت منبع</Text>
                  <Select
                    size="middle"
                    value={resourceStatus}
                    onChange={setResourceStatus}
                    style={{ width: '100%', marginTop: 4 }}
                  >
                    <Option value="planned">برنامه‌ریزی شده</Option>
                    <Option value="allocated">تخصیص داده شده</Option>
                    <Option value="used">استفاده شده</Option>
                    <Option value="completed">تکمیل شده</Option>
                  </Select>
                </div>
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }}>توضیحات (اختیاری)</Text>
                  <TextArea
                    rows={2}
                    maxLength={65535}
                    value={resourceDescription}
                    onChange={(e) => setResourceDescription(e.target.value)}
                    placeholder="توضیحات منبع..."
                    style={{ marginTop: 4, borderRadius: 10 }}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-2">
              {editingResourceId && (
                <Button
                  onClick={() => {
                    resetAllResourceFieldsAndType();
                  }}
                  style={{ borderRadius: 12, fontFamily: 'CustomFont' }}
                  disabled={resourceSubmitting}
                >
                  لغو و ایجاد منبع جدید
                </Button>
              )}
              <Button
                type="primary"
                className={primaryButtonClass}
                loading={resourceSubmitting}
                onClick={onSubmitResource}
                style={{ borderRadius: 12, fontFamily: 'CustomFont' }}
              >
                {editingResourceId ? 'به‌روزرسانی منبع' : 'ثبت منبع'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={attachmentModalOpen}
        onCancel={closeAttachmentModal}
        width={800}
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            پیوست‌های آیتم
          </span>
        }
        footer={null}
        destroyOnClose
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont', fontSize: 14 }}>
              لیست پیوست‌ها
            </Title>
          </div>

          {attachmentsLoading && itemAttachments.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Spin />
            </div>
          ) : itemAttachments.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ fontFamily: 'CustomFont' }}>هنوز پیوستی ثبت نشده است</span>}
            />
          ) : (
            <div className="space-y-3 max-h-64 overflow-auto">
              {itemAttachments.map((att) => {
                const createdDate = att.created_at ? new Date(att.created_at) : null;
                const dateStr = createdDate ? createdDate.toLocaleDateString('fa-IR') : '';
                const timeStr = createdDate
                  ? createdDate.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
                  : '';
                const fileSizeMB = att.file_size ? (att.file_size / 1024 / 1024).toFixed(2) : null;
                const fileSizeKB = att.file_size ? (att.file_size / 1024).toFixed(1) : null;

                return (
                  <div
                    key={att.id}
                    className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4 hover:shadow-sm transition-shadow bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-500 flex-shrink-0">
                        <FileOutlined style={{ fontSize: 18 }} />
                      </div>
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <Text
                          style={{ fontFamily: 'CustomFont', fontWeight: 500, fontSize: 13 }}
                          className="truncate"
                        >
                          {att.file_name}
                        </Text>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Text
                            type="secondary"
                            style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                          >
                            {fileSizeMB && Number(fileSizeMB) >= 1
                              ? `${fileSizeMB} MB`
                              : fileSizeKB
                              ? `${fileSizeKB} KB`
                              : ''}
                          </Text>
                          {att.description && (
                            <>
                              <span className="text-gray-300 dark:text-gray-600">•</span>
                              <Text
                                type="secondary"
                                className="truncate"
                                style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                              >
                                {att.description}
                              </Text>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 flex-shrink-0">
                      {att.created_at && (
                        <div className="flex flex-col items-end gap-0.5">
                          <Text
                            type="secondary"
                            style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                          >
                            {dateStr}
                          </Text>
                          <Text
                            type="secondary"
                            style={{ fontFamily: 'CustomFont', fontSize: 10 }}
                          >
                            {timeStr}
                          </Text>
                        </div>
                      )}
                      <Space size="small">
                        <Tooltip title="دانلود">
                          <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadAttachment(att.id, att.file_name)}
                            style={{ fontFamily: 'CustomFont' }}
                          />
                        </Tooltip>
                        <Tooltip title="ویرایش توضیحات">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                              setEditingAttachmentId(att.id);
                              setEditingAttachmentDescription(att.description || '');
                            }}
                            style={{ fontFamily: 'CustomFont' }}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="حذف پیوست"
                          description="آیا از حذف این پیوست مطمئن هستید؟"
                          okText="حذف"
                          cancelText="انصراف"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => handleDeleteAttachment(att.id)}
                        >
                          <Tooltip title="حذف">
                            <Button type="text" size="small" danger>
                              <DeleteOutlined />
                            </Button>
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pt-4 border-t border-gray-100 dark:border-gray-700 space-y-3">
            <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont', fontSize: 14 }}>
              افزودن پیوست جدید
            </Title>

            <Upload
              fileList={attachmentFileList}
              beforeUpload={(file) => {
                setAttachmentFileList([file]);
                return false;
              }}
              onRemove={() => {
                setAttachmentFileList([]);
              }}
            >
              <Button
                icon={<UploadOutlined />}
                style={{ borderRadius: 10, fontFamily: 'CustomFont' }}
              >
                انتخاب فایل
              </Button>
            </Upload>

            <div>
              <Text style={{ fontFamily: 'CustomFont' }}>توضیحات فایل (اختیاری)</Text>
              <TextArea
                rows={3}
                maxLength={500}
                value={attachmentDescription}
                onChange={(e) => setAttachmentDescription(e.target.value)}
                style={{ marginTop: 4, borderRadius: 10 }}
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="primary"
                className={primaryButtonClass}
                loading={attachmentUploading}
                disabled={attachmentUploading}
                style={{ borderRadius: 12, fontFamily: 'CustomFont' }}
                onClick={handleUploadAttachment}
              >
                آپلود فایل
              </Button>
            </div>
          </div>

          <Modal
            title={
              <span style={{ fontFamily: 'CustomFont' }}>
                ویرایش توضیحات پیوست
              </span>
            }
            open={!!editingAttachmentId}
            onCancel={() => {
              setEditingAttachmentId(null);
              setEditingAttachmentDescription('');
            }}
            onOk={handleSaveAttachmentDescription}
            okText="ذخیره"
            cancelText="انصراف"
            confirmLoading={editingAttachmentLoading}
          >
            <TextArea
              rows={4}
              value={editingAttachmentDescription}
              onChange={(e) => setEditingAttachmentDescription(e.target.value)}
              maxLength={500}
              style={{ borderRadius: 10, fontFamily: 'CustomFont' }}
            />
          </Modal>
        </div>
      </Modal>

      <Modal
        open={commentModalOpen}
        onCancel={closeCommentModal}
        width={900}
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            نظرات آیتم
          </span>
        }
        footer={null}
        destroyOnClose
      >
        <div>
          <div className="mt-4">
            {commentsLoading ? (
              <CommentsSkeleton count={3} />
            ) : itemComments.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ fontFamily: 'CustomFont' }}>
                    هنوز نظری ثبت نشده است
                  </span>
                }
              />
            ) : (
              <div className="space-y-4">
                {itemComments.map((c) => (
                  <div key={c.id} className="flex flex-col">
                    {/* کامنت اصلی */}
                    <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Avatar size="default" style={{ backgroundColor: '#1890ff' }}>
                            {String(c.user?.name || '').slice(0, 1).toUpperCase() || '?'}
                          </Avatar>
                          <div className="flex flex-col">
                            <Text style={{ fontFamily: 'CustomFont', fontWeight: 500 }}>
                              {c.user?.name || 'کاربر ناشناس'}
                            </Text>
                            {c.created_at && (
                              <Text
                                type="secondary"
                                style={{ fontFamily: 'CustomFont', fontSize: 12 }}
                              >
                                {new Date(c.created_at).toLocaleString('fa-IR')}
                              </Text>
                            )}
                          </div>
                        </div>
                        {c.is_edited && (
                          <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: 11 }}>
                            ویرایش شده
                          </Tag>
                        )}
                      </div>
                      <Text style={{ fontFamily: 'CustomFont', fontSize: '14px', lineHeight: '1.6' }}>
                        {renderContentWithMentions(c.content)}
                      </Text>
                      <div className="mt-2 flex justify-end gap-2">
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setCommentParentId(c.id)}
                          style={{ fontFamily: 'CustomFont', padding: '0 8px' }}
                        >
                          پاسخ
                        </Button>
                        <Popconfirm
                          title="حذف نظر"
                          description="آیا از حذف این نظر مطمئن هستید؟"
                          okText="حذف"
                          cancelText="انصراف"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => onDeleteItemComment(c.id)}
                        >
                          <Button
                            type="link"
                            size="small"
                            danger
                            loading={commentDeletingId === c.id}
                            style={{ fontFamily: 'CustomFont', padding: '0 8px' }}
                          >
                            حذف
                          </Button>
                        </Popconfirm>
                      </div>
                    </div>

                    {/* Reply ها */}
                    {c.replies && Array.isArray(c.replies) && c.replies.length > 0 && (
                      <CommentReplies replies={c.replies} depth={0} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
            {commentParentId && (
              <div className="mb-2 flex items-center justify-between">
                <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
                  در حال پاسخ به نظر شماره #{commentParentId}
                </Text>
                <Button
                  type="text"
                  size="small"
                  onClick={() => {
                    setCommentParentId(null);
                    setMentionMappings([]);
                    mentionRef.current?.clearMappings();
                  }}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  لغو پاسخ
                </Button>
              </div>
            )}
            <Text style={{ fontFamily: 'CustomFont' }} strong>
              افزودن نظر جدید
            </Text>
            <div style={{ marginTop: 8 }}>
              <MentionTextArea
                ref={mentionRef}
                value={commentContent}
                onChange={(newValue, mappings) => {
                  setCommentContent(newValue);
                  if (mappings) {
                    setMentionMappings(mappings);
                  }
                }}
                onSyncStatusChange={(isSyncing) => {
                  setIsMentionSyncing(isSyncing);
                }}
                rows={4}
                maxLength={5000}
                placeholder="متن نظر خود را وارد کنید... (برای منشن کردن از @ استفاده کنید)"
              />
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                type="primary"
                onClick={onSubmitItemComment}
                loading={commentSubmitting}
                disabled={isMentionSyncing}
                className={primaryButtonClass}
                style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
              >
                {isMentionSyncing ? 'در حال همگام‌سازی...' : 'ثبت نظر'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

